import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Hash, Lock, User, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { isValidUsPhone, isValidEmail, digitsOnly, PHONE_ERROR, EMAIL_ERROR, PHONE_MAX_DIGITS } from "../../utils/validation";
import { useInfinityCustomer } from "../../context/InfinityCustomerContext";
import { getImageUrl } from "../../utils/imageUtils";
import InfinitySlider from "./InfinitySlider";

const PICK_KEY = "it_customer_pick_product";

/**
 * Passwordless login (TR-10 report 4) — a polished split screen: an elegant
 * memorial hero on the LEFT (gradient + floating keepsake card + headline + dots)
 * and a clean, spacious form on the RIGHT (brand, big heading, roomy icon inputs,
 * prominent button). The emailed link carries order_number + pin in the query.
 */
export default function InfinityLogin() {
  const { login, theme, loginSlider, featuredProducts, basePath } = useInfinityCustomer();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  // Admin-curated slides, else featured Infinity products, mapped to keepsake cards.
  const slides = (loginSlider && loginSlider.length)
    ? loginSlider
    : (featuredProducts || [])
        .filter((p) => p.image_url)
        .map((p) => ({ product_id: p.product_id, background_image_url: p.image_url, text: p.name }));

  // Read the link params leniently. Links already in the wild carry `PIN=` (the admin
  // views built them uppercase until TR-21), and mail clients that auto-linkify an
  // escaped URL turn `&pin` into `&amp;pin` — URLSearchParams matches neither exactly.
  const linkParam = (key) => {
    for (const [k, v] of params.entries()) {
      if (k.toLowerCase().replace(/^amp;/, "") === key) return v;
    }
    return "";
  };

  const [orderId, setOrderId] = useState(linkParam("order_id"));
  const [pin, setPin] = useState(linkParam("pin"));
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [logoErr, setLogoErr] = useState(false);

  useEffect(() => {
    setLogoErr(false);
  }, [theme?.logo_url]);

  const submit = async (e) => {
    e.preventDefault();
    if (!orderId || !pin) return toast.error("Order or Case Number and PIN are required");
    if (!name) return toast.error("Please enter your name");
    // Phone and email are optional here — the reference and PIN are the
    // credentials — but reject a malformed one rather than storing it against
    // the customer, where it becomes an order confirmation that never arrives.
    if (phone && !isValidUsPhone(phone)) return toast.error(PHONE_ERROR);
    if (email && !isValidEmail(email)) return toast.error(EMAIL_ERROR);
    setBusy(true);
    try {
      await login({ order_id: orderId, pin, name, phone, email });
      const picked = sessionStorage.getItem(PICK_KEY);
      sessionStorage.removeItem(PICK_KEY);
      navigate(picked ? `${basePath}/product/${picked}` : `${basePath}/home`);
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const onPickSlide = (productId) => {
    if (productId) sessionStorage.setItem(PICK_KEY, productId);
    toast.info("Log in to view this keepsake");
  };

  const primary = theme.primary_color || "#2E72BC";
  const secondary = theme.secondary_color || "#0C447C";

  // Fullscreen edge-to-edge layout matching viewport height
  return (
    <div className="relative w-full h-screen overflow-hidden flex flex-col lg:flex-row bg-slate-900">
      {/* Full-width Slider background layer across the entire screen */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{ background: `linear-gradient(135deg, ${primary}, ${secondary})` }}
      >
        <InfinitySlider slides={slides} theme={theme} onPick={onPickSlide} />
      </div>

      {/* Spacer on desktop so slider text & card stay visible on the left (70% width) */}
      <div className="hidden lg:block lg:w-[70%] pointer-events-none" />

      {/* Right — compact login form panel (30% width) with 50% transparency */}
      <div className="relative z-20 w-full lg:w-[30%] h-full flex flex-col items-center justify-center p-6 sm:p-8 lg:p-10 bg-white/[0.50] backdrop-blur-md border-t lg:border-t-0 lg:border-l border-white/40 shadow-2xl overflow-y-auto">
        <div className="w-full max-w-sm my-auto">
          {/* Brand Logo */}
          <div className="mb-6">
            {theme.logo_url && !logoErr ? (
              <img
                src={getImageUrl(theme.logo_url)}
                alt="Infinity Touch"
                className="h-14 sm:h-16 w-auto max-w-[280px] object-contain drop-shadow-sm"
                onError={() => setLogoErr(true)}
              />
            ) : (
              <div className="flex items-center gap-2.5">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white font-bold shadow-md text-base" style={{ backgroundColor: primary }}>IT</div>
                <span className="text-xl font-bold text-slate-900">Infinity Touch</span>
              </div>
            )}
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Welcome</h1>
          <p className="text-slate-700 mt-1 mb-6 text-xs sm:text-sm font-medium">View your loved one's memorial and order keepsakes made from their print.</p>

          <form onSubmit={submit} className="space-y-3 sm:space-y-3.5">
            {!linkParam("order_id") ? (
              <IconInput icon={Hash} label="Order or Case Number" value={orderId} onChange={setOrderId} placeholder="ORD-1234 or CASE-1234" />
            ) : null}
            {!linkParam("pin") ? (
              <IconInput icon={Lock} label="PIN" value={pin} onChange={setPin} placeholder="4-digit PIN" />
            ) : null}
            <IconInput icon={User} label="Name" value={name} onChange={setName} placeholder="Jane Smith" />
            <IconInput icon={Phone} label="Phone" value={phone} onChange={setPhone} placeholder="1234567890"
              type="tel" maxLength={PHONE_MAX_DIGITS} sanitize={(v) => digitsOnly(v, PHONE_MAX_DIGITS)} />
            <IconInput icon={Mail} label="Email" value={email} onChange={setEmail} placeholder="family@example.com" type="email" />
            <Button type="submit" disabled={busy} className="w-full h-11 text-white rounded-xl text-sm font-semibold shadow-lg transition-transform active:scale-[0.99] mt-2"
              style={{ backgroundColor: primary }}>
              {busy ? "Please wait…" : "Continue"}
            </Button>
          </form>

          {/* Helper text below Continue button */}
          <p className="text-xs text-slate-700 font-medium mt-4 text-center">
            Use the Order or Case Number and PIN given to you by the funeral home.
          </p>

          {/* Footer text below the login form */}
          <footer className="text-center text-xs text-slate-700 font-medium mt-8 pt-4 border-t border-slate-900/15">
            © {new Date().getFullYear()} Infinity Touch. All rights reserved.
          </footer>
        </div>
      </div>
    </div>
  );
}

/*
 * `sanitize` runs on every keystroke, so a field can reject characters outright
 * rather than accepting them and complaining on submit — see the phone box,
 * which simply will not hold a letter.
 */
function IconInput({ icon: Icon, label, value, onChange, placeholder, type, maxLength, sanitize }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-slate-700">{label}</label>
      <div className="relative">
        <Icon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input value={value} onChange={(e) => onChange(sanitize ? sanitize(e.target.value) : e.target.value)}
          placeholder={placeholder} type={type} maxLength={maxLength}
          className="pl-10 h-12 rounded-xl bg-white/[0.10] border-white/60 focus:bg-white/[0.65] focus:border-white text-slate-900 placeholder:text-slate-500 shadow-sm backdrop-blur-sm transition-all" />
      </div>
    </div>
  );
}
